/**
 * web/stylesCss.ts
 * Raw CSS payload for the shared stylesheet.
 *
 * Split out from `web/styles.ts` so the injection runtime and the CSS
 * data each stay under the AGENTS.md 300-line file-length cap. Import
 * from `./styles` (or `../styles`), never from this file directly —
 * the injection guard in `styles.ts` is the only supported entry point.
 *
 * @module web/stylesCss
 */

export const SHARED_CSS = `
:root {
  --color-bg: #090f1b;
  --color-bg-soft: #11192b;
  --color-card: #1e3a5f;
  --color-card-locked: #0d1a2e;
  --color-gold: #f5c842;
  --color-steel: #8ab4d4;
  --color-white: #ffffff;
  --color-danger: #e84040;
  --color-success: #4cdd7a;
  --color-overlay: rgba(0, 0, 0, 0.65);
  --color-muted: #888888;
  color-scheme: dark;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  background: var(--color-bg);
  color: var(--color-white);
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
}
* { box-sizing: border-box; }

#app { min-height: 100vh; display: flex; flex-direction: column; }

.sf-screen {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  gap: 1rem;
  max-width: 960px;
  width: 100%;
  margin: 0 auto;
}
.sf-screen-title {
  margin: 0;
  font-size: 2rem;
  color: var(--color-gold);
  letter-spacing: 0.06em;
  text-align: center;
}
.sf-screen-subtitle {
  margin: 0;
  color: var(--color-steel);
  font-size: 1rem;
  text-align: center;
}

/* Buttons (minimum 60×60 px to satisfy AGENTS.md touch-target rule). */
.sf-btn {
  min-width: 180px;
  min-height: 60px;
  padding: 0.85rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  border: 2px solid var(--color-steel);
  background: var(--color-bg-soft);
  color: var(--color-white);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
}
.sf-btn:hover { background: var(--color-card); }
.sf-btn:active { transform: translateY(1px); }
.sf-btn-primary {
  background: var(--color-gold);
  color: var(--color-bg);
  border-color: var(--color-gold);
}
.sf-btn-primary:hover { background: #ffd664; }
.sf-btn-quit { border-color: var(--color-danger); color: var(--color-danger); }
.sf-btn-quit:hover { background: rgba(232, 64, 64, 0.12); }
.sf-btn-back {
  min-width: 60px;
  min-height: 60px;
  align-self: flex-start;
  padding: 0.5rem 0.9rem;
  font-size: 0.95rem;
}

/* Toggle row for the settings screen. */
.sf-toggle-row {
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; padding: 0.75rem 1rem; border-radius: 10px;
  background: var(--color-bg-soft);
  border: 1px solid #233a55;
}
.sf-toggle-row label { font-size: 1rem; color: var(--color-white); }
.sf-toggle-row input[type="checkbox"] {
  width: 60px; height: 60px; cursor: pointer; accent-color: var(--color-gold);
}
.sf-settings-section { width: 100%; display: flex; flex-direction: column; gap: 0.5rem; }
.sf-settings-section h3 {
  margin: 0.75rem 0 0.25rem 0; font-size: 0.9rem; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--color-steel);
}

/* World map / character cards. */
.sf-card-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem; width: 100%;
}
.sf-card {
  background: var(--color-card);
  border-radius: 12px;
  padding: 1rem;
  display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
  cursor: pointer;
  border: 2px solid transparent;
  min-height: 200px;
  position: relative;
  overflow: hidden;
}
.sf-card:hover { border-color: var(--color-gold); }
.sf-card-locked {
  background: var(--color-card-locked);
  cursor: not-allowed;
  opacity: 0.55;
}
.sf-card-locked:hover { border-color: transparent; }
.sf-card-title { margin: 0; color: var(--color-gold); font-size: 1.1rem; }
.sf-card-subtitle { margin: 0; color: var(--color-steel); font-size: 0.85rem; }
.sf-card-meta { margin: 0; color: var(--color-white); font-size: 0.85rem; opacity: 0.8; }
.sf-card-preview {
  width: 120px; height: 120px;
  image-rendering: pixelated;
  background-color: var(--color-bg-soft);
  border-radius: 8px;
}

/* Cell wrapping each world card + its stage shortcut row in the grid. */
.sf-world-cell {
  display: flex; flex-direction: column; gap: 0.5rem;
}

/* Per-world accent strip pinned to the top of the card. Background colour
   is set inline from WorldEntry.accent so each constellation reads at
   a glance. */
.sf-card-accent-strip {
  position: absolute; top: 0; left: 0; right: 0;
  height: 6px; border-radius: 12px 12px 0 0;
  pointer-events: none;
}

/* Shard progress bar: dim track + accent-coloured fill. Track is full
   width of the card content; fill width is set inline as a percentage. */
.sf-card-shard-label { opacity: 1; margin-top: 0.25rem; }
.sf-card-shard-track {
  width: 100%; height: 8px; border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.sf-card-shard-fill {
  height: 100%; border-radius: 4px;
  transition: width 200ms ease-out;
}

/* Stage clear stars (filled-or-empty unicode stars). Colour set inline
   from WorldEntry.accent so each world reads with its own palette. */
.sf-card-stage-stars {
  margin-top: 0.25rem; letter-spacing: 0.15em;
  font-size: 1rem; opacity: 1;
}

/* Stage shortcut row inside a world card. */
.sf-stage-row { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.5rem; }
.sf-stage-btn {
  min-width: 60px; min-height: 60px; padding: 0.4rem 0.7rem;
  border-radius: 6px; background: var(--color-bg-soft);
  color: var(--color-white); border: 1px solid var(--color-steel);
  font-size: 0.85rem; cursor: pointer;
}
.sf-stage-btn:hover { background: var(--color-gold); color: var(--color-bg); }
.sf-stage-btn[disabled] { opacity: 0.4; cursor: not-allowed; }

/* Game host layout. */
.sf-game-root {
  position: fixed; inset: 0; background: var(--color-bg);
  overflow: hidden; display: flex; flex-direction: column;
}
.sf-game-canvas {
  display: block; image-rendering: pixelated;
  width: 100%; height: 100%; flex: 1 1 auto;
}
.sf-game-pause-btn {
  position: absolute; top: 12px; right: 12px; z-index: 70;
  min-width: 60px; min-height: 60px; padding: 0.4rem 0.7rem;
  border-radius: 8px; border: 1px solid var(--color-steel);
  background: var(--color-bg-soft); color: var(--color-white); cursor: pointer;
}

/* HUD strip. */
.sf-hud-strip {
  position: absolute; top: 0; left: 0; right: 0;
  background: rgba(9, 15, 27, 0.55);
  padding: 8px 12px; display: flex; gap: 1rem; flex-wrap: wrap;
  pointer-events: none; z-index: 60; font-variant-numeric: tabular-nums;
}
.sf-hud-strip span { color: var(--color-white); font-size: 0.95rem; }
.sf-hud-hearts { color: var(--color-danger) !important; letter-spacing: 2px; }
.sf-hud-shards { color: var(--color-gold) !important; }
.sf-hud-weapon { color: var(--color-gold) !important; }
.sf-hud-timer { margin-left: auto; color: var(--color-steel) !important; }
.sf-hud-space .sf-hud-world { color: var(--color-gold) !important; }

/* Touch controls. */
.sf-touch-controls {
  position: absolute; left: 0; right: 0; bottom: 0;
  display: flex; justify-content: space-between; align-items: flex-end;
  padding: 16px 20px; pointer-events: none; z-index: 65;
}
.sf-dpad {
  display: grid;
  grid-template-columns: 64px 64px 64px;
  grid-template-rows: 64px 64px;
  gap: 6px;
  pointer-events: auto;
}
.sf-dpad-btn {
  width: 64px; height: 64px;
  border-radius: 12px; border: 1px solid var(--color-steel);
  background: rgba(30, 58, 95, 0.85); color: var(--color-white);
  font-size: 1.4rem; cursor: pointer;
}
.sf-dpad-left  { grid-column: 1; grid-row: 1; }
.sf-dpad-right { grid-column: 3; grid-row: 1; }
.sf-dpad-down  { grid-column: 2; grid-row: 2; }
.sf-dpad-up    { grid-column: 2; grid-row: 1; }
.sf-actions { display: flex; gap: 12px; pointer-events: auto; }
.sf-action-btn {
  min-width: 80px; min-height: 80px;
  border-radius: 50%; border: 1px solid var(--color-gold);
  background: rgba(245, 200, 66, 0.18); color: var(--color-gold);
  font-weight: 700; font-size: 0.9rem; cursor: pointer;
}

/* Overlays. */
.sf-overlay-backdrop {
  position: fixed; inset: 0;
  background: var(--color-overlay);
  display: flex; align-items: center; justify-content: center;
  z-index: 100;
}
.sf-overlay-card {
  background: var(--color-card); border-radius: 14px;
  padding: 2rem; min-width: 280px; max-width: 420px;
  display: flex; flex-direction: column; gap: 0.85rem; align-items: center;
  border: 2px solid var(--color-gold);
}
.sf-overlay-title { margin: 0; color: var(--color-gold); font-size: 1.6rem; }
.sf-overlay-subtitle { margin: 0; color: var(--color-steel); }
.sf-overlay-icon { font-size: 3rem; color: var(--color-gold); }
.sf-overlay-stars { font-size: 1.8rem; color: var(--color-gold); }
.sf-overlay-stats { list-style: none; padding: 0; margin: 0; text-align: center; }
.sf-overlay-stats li { padding: 0.15rem 0; color: var(--color-white); }
.sf-overlay-actions { display: flex; gap: 0.6rem; flex-wrap: wrap; justify-content: center; }
.sf-intro-text {
  font-size: 1rem; color: var(--color-white); text-align: center;
  line-height: 1.4; min-height: 4em;
}

/* Ending sequence — shares the overlay card, diverges per beat. */
.sf-ending-text {
  font-size: 1rem; color: var(--color-white); text-align: center;
  line-height: 1.5; min-height: 5em;
  /* CREDITS_TEXT carries its own line breaks. */
  white-space: pre-line;
}
.sf-ending-closing { font-style: italic; color: var(--color-gold); }
.sf-ending-credits {
  color: var(--color-gold); letter-spacing: 0.08em; font-size: 1.15rem;
}

/* Bark bubble above HUD. */
.sf-bark {
  position: absolute; top: 48px; left: 0; right: 0;
  display: flex; justify-content: center; pointer-events: none; z-index: 80;
}
.sf-bark-bubble {
  background: rgba(10, 15, 30, 0.85);
  border: 1px solid rgba(245, 200, 66, 0.4);
  border-radius: 8px; padding: 6px 14px;
  color: var(--color-gold); font-style: italic;
  max-width: 340px; text-align: center; font-size: 0.95rem;
}
`;
